/**
 * CR500 Phase 4B/4C: PromoBadgesBlock
 * 
 * Renders the promo badges row (membership, corporate, bulk, etc.).
 * Uses getPromoBadges helper for single source of truth.
 * 
 * Phase 4C additions:
 * - Debug diagnostics for corporate detection
 * - maxBadges and order support
 * - Suppression reasons in debug mode
 */

import React from "react";
import { Building2, HelpCircle, Lock, Info, AlertCircle } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { 
  getPromoBadgesWithDiagnostics, 
  getCorporateDebugInfo,
  type PromoBadge, 
  type PromoBadgesVisibilityConfig,
  DEFAULT_BADGE_ORDER,
} from "@/lib/deal-cards/helpers/getPromoBadges";
import { isV2DebugEnabled } from "@/lib/deal-cards/helpers";
import { cn } from "@/lib/utils";

// ================================================================
// BADGE STYLING
// ================================================================

const BADGE_STYLES: Record<PromoBadge["variant"], string> = {
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  purple: "bg-purple-100 text-purple-800 border-purple-200",
  slate: "bg-slate-100 text-slate-800 border-slate-200",
  muted: "bg-black/5 text-black/80",
};

const BADGE_ICONS: Record<PromoBadge["icon"], React.ElementType> = {
  lock: Lock,
  building: Building2,
  help: HelpCircle,
  info: Info,
  alert: AlertCircle,
};

// Debug mode uses centralized guard from helpers/debugGuard.ts

// ================================================================
// COMPONENT
// ================================================================

interface PromoBadgesBlockProps extends DealCardBlockProps<Deal> {
  /** Optional visibility config override from Layout Studio */
  visibilityConfig?: PromoBadgesVisibilityConfig;
}

export const PromoBadgesBlock: React.FC<PromoBadgesBlockProps> = ({
  deal,
  block,
  context,
  visibilityConfig,
}) => {
  const debugMode = isV2DebugEnabled();

  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  // Merge block config with visibilityConfig prop
  const config: PromoBadgesVisibilityConfig = {
    enabled: true,
    showMembership: true,
    showCorporate: true,
    showUnclassified: true,
    showBulkDeal: true,
    showDetailsVary: true,
    showConfirmOnSite: true,
    // Apply block.config overrides if present
    ...(block.config as Partial<PromoBadgesVisibilityConfig> | undefined),
    // Apply prop overrides last (highest priority)
    ...visibilityConfig,
  };

  // Get badges with full diagnostics
  const { badges, eligibleBadges, suppressedBadges, debug } = getPromoBadgesWithDiagnostics(deal, config);
  
  // Get corporate debug info for detailed diagnostics
  const corporateDebug = debugMode ? getCorporateDebugInfo(deal) : null;

  // No badges = don't render (unless debug mode)
  if (badges.length === 0 && !debugMode) {
    return null;
  }

  return (
    <div 
      className="flex flex-col gap-2"
      data-block="promo_badges"
      data-block-id={block.id}
      data-component-key="promo_badges"
      data-badge-count={badges.length}
      data-badge-keys={badges.map(b => b.key).join(",")}
      data-eligible-count={eligibleBadges.length}
      data-suppressed-count={suppressedBadges.length}
    >
      {/* Badge row */}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {badges.map((badge) => {
            const Icon = BADGE_ICONS[badge.icon];
            const hasBorder = badge.variant !== "muted";
            
            return (
              <span
                key={badge.key}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold",
                  BADGE_STYLES[badge.variant],
                  hasBorder && "border"
                )}
                data-badge-key={badge.key}
              >
                <Icon className="w-3 h-3" />
                {badge.label}
              </span>
            );
          })}
        </div>
      )}
      
      {/* Debug diagnostics (only in ?dealCardV2debug=1 mode) */}
      {debugMode && (
        <div className="bg-slate-100 border border-slate-300 rounded p-2 text-[10px] font-mono space-y-1">
          <div className="font-bold text-slate-700">🔍 Promo Badges Debug</div>
          
          {/* Eligible badges */}
          <div className="text-slate-600">
            <span className="font-semibold">Eligible:</span>{" "}
            {eligibleBadges.length > 0 
              ? eligibleBadges.map(b => b.key).join(", ")
              : "(none)"}
          </div>
          
          {/* Displayed badges */}
          <div className="text-green-700">
            <span className="font-semibold">Displayed:</span>{" "}
            {badges.length > 0 
              ? badges.map(b => b.key).join(", ")
              : "(none)"}
          </div>
          
          {/* Suppressed badges */}
          {suppressedBadges.length > 0 && (
            <div className="text-orange-600">
              <span className="font-semibold">Suppressed:</span>
              {suppressedBadges.map(({ badge, reason }) => (
                <div key={badge.key} className="ml-2">
                  • {badge.key}: {reason}
                </div>
              ))}
            </div>
          )}
          
          {/* Corporate signals */}
          {corporateDebug && (
            <div className={corporateDebug.isCorporate ? "text-purple-700" : "text-slate-500"}>
              <span className="font-semibold">Corporate:</span>{" "}
              {corporateDebug.isCorporate ? "YES" : "NO"}
              <div className="ml-2 space-y-0.5">
                {Object.entries(corporateDebug.signals).map(([key, value]) => (
                  <div key={key} className={value ? "text-purple-600" : "text-slate-400"}>
                    {value ? "✓" : "✗"} {key}
                    {value && corporateDebug.rawValues[key.replace(/([A-Z])/g, '_$1').toLowerCase()] && (
                      <span className="ml-1 text-purple-500">
                        = {String(corporateDebug.rawValues[key.replace(/([A-Z])/g, '_$1').toLowerCase()])}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Config */}
          <div className="text-slate-500">
            <span className="font-semibold">Config:</span>{" "}
            maxBadges={config.maxBadges ?? "∞"}, 
            order=[{(config.order ?? DEFAULT_BADGE_ORDER).join(", ")}]
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoBadgesBlock;
